import React, { useState, useEffect } from "react";
import Layout from "../../components/ui/Layout";
import { useParams } from "react-router-dom";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Cookies from "js-cookie";
import PayPal from "../../assets/img/paypal.svg";
import Visa from "../../assets/img/visa.svg";
import Mastercard from "../../assets/img/mastercard.svg";
import Amex from "../../assets/img/amex.svg";
import { CiBank } from "react-icons/ci";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiUser, FiMapPin, FiPhone, FiMail, FiCreditCard, FiClipboard } from "react-icons/fi";
import CheckoutForm from "../../utils/CheckoutForm";
import NotFound from "../NotFound";
import { useNavigate, Link } from "react-router-dom";

const steps = [
  { id: 0, title: "Dati Personali", icon: <FiUser /> },
  { id: 1, title: "Metodo Di Pagamento", icon: <FiCreditCard /> },
  { id: 2, title: "Completa Pagamento", icon: <FiCheckCircle /> },
];

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ 
    opacity: 1, 
    y: 0, 
    transition: { 
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const slideVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.4
    }
  },
  exit: { 
    opacity: 0, 
    x: 20,
    transition: {
      duration: 0.3
    }
  }
};

function Payment() {
  const [showComponent, setShowComponent] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set()); // Track completed steps
  const [paymentData, setPaymentData] = useState(null);
  const [orderData, setOrderDAta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const { paymentId } = useParams();
  const navigate = useNavigate();

  function handleNextStep() {
    setCompletedSteps(prev => new Set(prev).add(showComponent)); // Mark current step as completed
    
    // Animation effect: move up slightly then to next component
    setShowComponent(showComponent + 1);
  }

  const handleStepClick = (index) => {
    if (completedSteps.has(index - 1) || index <= showComponent) { 
      setShowComponent(index); // Only allow navigation to completed or previous steps
    }
  };

  function PersonalData() {
    const [userData, setUserData] = useState({
      firstname: "",
      lastname: "",
      phone: "",
      street: "",
      city: "",
      postcode: "",
      country: "",
    });
    const [initialUserData, setInitialUserData] = useState(null); // Store initial data
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
  
    async function fetchUserData() {
      try {
        const accessToken = Cookies.get("accessToken");
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}user/userData?accessToken=${accessToken}`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setUserData(data);
        setInitialUserData(data); // Set initial data
        setLoading(false);
      } catch (error) {
        setErrorMessage("Failed to fetch user data. Please try again later.");
        setLoading(false);
      }
    }
  
    useEffect(() => {
      fetchUserData();
    }, []);
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setUserData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
    
      try {
        const accessToken = Cookies.get("accessToken");
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}user/updateUserData?accessToken=${accessToken}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });
    
        if (!response.ok) {
          throw new Error("Failed to update user data");
        }
    
        const updatedData = await response.json();
        setUserData(updatedData); // Update local state with response data
        setInitialUserData(updatedData); // Reset initial data
        setSuccessMessage("Dati aggiornati con successo");
        
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } catch (error) {
        console.error(error);
        setErrorMessage("Errore durante l'aggiornamento dei dati. Riprova più tardi.");
        
        setTimeout(() => {
          setErrorMessage(null);
        }, 3000);
      }
    };
  
    // Function to check if any data has been modified
    const hasChanges = JSON.stringify(userData) !== JSON.stringify(initialUserData);

    if (!orderData || !paymentData) {
      return null; // Don't render anything until parent data is loaded
    }
  
    if (loading) {
      return (
        <motion.div 
          initial={{ opacity: 1 }} // Changed from 0 to 1
          animate={{ opacity: 1 }}
          className="flex justify-center items-center h-64"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#406580]"></div>
        </motion.div>
      );
    }
  
    if (errorMessage) {
      return (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-red-600 text-lg p-8 bg-white rounded-lg shadow-md"
        >
          {errorMessage}
        </motion.div>
      );
    }
  
    return (
      <motion.div
        variants={containerVariants}
        className="w-full max-w-4xl mx-auto"
      >
        <motion.div 
          variants={fadeIn}
          custom={0}
          className="bg-white p-8 md:p-10 border border-gray-200 rounded-xl shadow-sm mb-6"
        >
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 inria-serif-regular mb-6 flex items-center">
            <FiUser className="mr-3 text-[#406580]" /> Dati Personali
          </h2>

          {/* Success Message */}
          <AnimatePresence>
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-green-50 p-4 rounded-lg border border-green-200 mb-6"
              >
                <div className="flex">
                  <FiCheckCircle className="text-green-500 mr-3 mt-0.5" />
                  <p className="text-green-700">{successMessage}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-red-50 p-4 rounded-lg border border-red-200 mb-6"
              >
                <div className="flex">
                  <div className="text-red-500 mr-3">⚠️</div>
                  <p className="text-red-700">{errorMessage}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={fadeIn} custom={1} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiUser className="mr-2 text-gray-500" />
                Nome
              </label>
              <input
                type="text"
                name="firstname"
                value={userData.firstname}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            <motion.div variants={fadeIn} custom={2} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiUser className="mr-2 text-gray-500" />
                Cognome
              </label>
              <input
                type="text"
                name="lastname"
                value={userData.lastname}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            <motion.div variants={fadeIn} custom={3} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiPhone className="mr-2 text-gray-500" />
                Telefono
              </label>
              <input
                type="text"
                name="phone"
                value={userData.phone}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            <motion.div variants={fadeIn} custom={4} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiMapPin className="mr-2 text-gray-500" />
                Indirizzo
              </label>
              <input
                type="text"
                name="street"
                value={userData.street}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            <motion.div variants={fadeIn} custom={5} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiMapPin className="mr-2 text-gray-500" />
                Città
              </label>
              <input
                type="text"
                name="city"
                value={userData.city}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            <motion.div variants={fadeIn} custom={6} className="flex flex-col">
              <label className="flex items-center text-gray-700 font-medium mb-1.5">
                <FiMapPin className="mr-2 text-gray-500" />
                CAP
              </label>
              <input
                type="text"
                name="postcode"
                value={userData.postcode}
                onChange={handleChange}
                className="mt-1 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-all"
              />
            </motion.div>

            {hasChanges && (
              <motion.div 
                variants={fadeIn}
                custom={7}
                className="col-span-full w-full flex justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-6 py-2.5 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] shadow-sm transition-all"
                >
                  Salva Modifiche
                </motion.button>
              </motion.div>
            )}
          </form>
        </motion.div>
      </motion.div>
    );
  }

  function PaymentMethod() {
    const handlePaymentMethodChange = (method) => {
      setSelectedPaymentMethod(method);
    };
  
    return (
      <motion.div 
        variants={containerVariants}
        className="flex flex-col lg:flex-row lg:space-x-8 mx-auto max-w-4xl space-y-6 lg:space-y-0"
      >
        {/* Left Section: Payment Method Selection */}
        <motion.div 
          variants={fadeIn} 
          custom={0}
          className="flex-1 space-y-6"
        >
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 inria-serif-regular flex items-center">
            <FiCreditCard className="mr-3 text-[#406580]" /> Metodi di Pagamento
          </h2>
          
          <div className="space-y-4">
            <motion.button
              variants={fadeIn}
              custom={1}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handlePaymentMethodChange("creditCard")}
              className={`w-full flex items-center justify-between px-5 py-3.5 rounded-lg transition-all duration-200 ${
                selectedPaymentMethod === "creditCard"
                  ? "bg-[#406580] text-white shadow-md"
                  : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-center gap-x-3">
                <div className="flex">
                  <motion.img 
                    whileHover={{ y: -2 }}
                    src={Visa} 
                    alt="Visa Logo" 
                    className="w-8 h-8 mr-1" 
                  />
                  <motion.img 
                    whileHover={{ y: -2 }}
                    src={Mastercard} 
                    alt="Mastercard Logo" 
                    className="w-8 h-8 mr-1" 
                  />
                  <motion.img 
                    whileHover={{ y: -2 }}
                    src={Amex} 
                    alt="American Express Logo" 
                    className="w-8 h-8" 
                  />
                </div>
                <span className="font-medium">Carta di Credito / Debito</span>
              </div>

              <span className={`text-sm ${selectedPaymentMethod === "creditCard" ? "text-white/80" : "text-gray-500"}`}>
                2-10 Min.
              </span>
            </motion.button>

            <motion.button
              variants={fadeIn}
              custom={2}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handlePaymentMethodChange("bankTransfer")}
              className={`w-full flex items-center justify-between px-5 py-3.5 rounded-lg transition-all duration-200 ${
                selectedPaymentMethod === "bankTransfer"
                  ? "bg-[#406580] text-white shadow-md"
                  : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-center gap-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedPaymentMethod === "bankTransfer" ? "bg-white/20" : "bg-gray-100"}`}>
                  <CiBank className={`w-5 h-5 ${selectedPaymentMethod === "bankTransfer" ? "text-white" : "text-[#406580]"}`} />
                </div>
                <span className="font-medium">Bonifico Bancario</span>
              </div>

              <span className={`text-sm ${selectedPaymentMethod === "bankTransfer" ? "text-white/80" : "text-gray-500"}`}>
                1-5 Giorni
              </span>
            </motion.button>

            <motion.button
              variants={fadeIn}
              custom={3}
              disabled
              className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
            >
              <div className="flex items-center gap-x-3">
                <motion.img src={PayPal} alt="PayPal Logo" className="w-8 h-8 opacity-60" />
                <span className="font-medium">PayPal</span>
              </div>

              <span className="text-sm">Disabilitato</span>
            </motion.button>
          </div>
        </motion.div>
  
        {/* Right Section: Order Summary */}
        <motion.div
          variants={fadeIn}
          custom={4}
          className="flex-1"
        >
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold tracking-tight text-gray-900 inria-serif-regular mb-4 flex items-center">
              <FiClipboard className="mr-3 text-[#406580]" /> Riepilogo Ordine
            </h2>
            
            <div className="space-y-3">
              <motion.div 
                variants={fadeIn}
                custom={5}
                className="flex items-center justify-between py-2 border-b border-gray-100"
              >
                <span className="text-gray-600">ID Ordine</span>
                <span className="font-medium text-gray-900">{orderData.stripePaymentId}</span>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                custom={6}
                className="flex items-center justify-between py-2 border-b border-gray-100"
              >
                <span className="text-gray-600">Totale</span>
                <span className="font-medium text-gray-900 text-lg">€{orderData.paymentTotal.toFixed(2)}</span>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                custom={7}
                className="flex items-center justify-between py-2 border-b border-gray-100"
              >
                <span className="text-gray-600">Creazione Ordine</span>
                <span className="font-medium text-gray-900">{new Date(orderData.orderCreationTime).toLocaleString()}</span>
              </motion.div>
              
              {orderData.orderProducts && (
                <motion.div
                  variants={fadeIn}
                  custom={8}
                  className="pt-3"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">Articoli ({orderData.orderProducts.length})</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {orderData.orderProducts.map((product, index) => (
                      <motion.div 
                        key={index}
                        variants={fadeIn}
                        custom={index + 9}
                        className="flex items-center p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="h-10 w-10 bg-gray-200 rounded overflow-hidden mr-3 flex-shrink-0">
                          <img 
                            src={product.imageUrl.startsWith('http') ? product.imageUrl : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${product.imageUrl}`}
                            alt={product.productName}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{product.productName}</p>
                          <div className="flex justify-between mt-1">
                            <span className="text-xs text-gray-500">Quantità: {product.quantity}</span>
                            <span className="text-xs font-medium">€{product.productPrice.toFixed(2)}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  const CompletePayment = () => {
    return(
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full flex justify-center"
      >
        <motion.div 
          variants={fadeIn}
          custom={0}
          className="w-full max-w-4xl bg-white py-8 rounded-xl border border-gray-200 shadow-sm"
        >
          {loading ? (
            // Display spinner while loading
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#406580]"></div>
            </div>
          ) : errorMessage ? (
            // Display error message if the fetch fails
            <motion.div 
              variants={fadeIn}
              custom={1}
              className="bg-red-50 text-red-700 p-6 rounded-lg m-6 text-center"
            >
              <p className="font-medium mb-2">Si è verificato un errore</p>
              <p>{errorMessage}</p>
            </motion.div>
          ) : (
            selectedPaymentMethod === "creditCard" ? (
              <motion.div
                variants={fadeIn}
                custom={2}
                className="px-8"
              >
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 inria-serif-regular mb-6 text-center">
                  Completa il Pagamento
                </h2>
                <p className="mb-6 text-gray-600 text-center">
                  Inserisci i dati della tua carta di credito per completare l'ordine in modo sicuro.
                </p>
                <Elements stripe={stripePromise} options={options}>
                  <CheckoutForm paymentId={paymentData.stripePaymentId} />
                </Elements>
              </motion.div>
            ) : selectedPaymentMethod === "bankTransfer" ? (
              <motion.div
                variants={fadeIn}
                custom={2}
                className="mx-8"
              >
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 inria-serif-regular mb-4 flex items-center">
                  <CiBank className="mr-3 text-[#406580]" /> Istruzioni Bonifico Bancario
                </h2>
                
                <motion.p 
                  variants={fadeIn}
                  custom={3}
                  className="text-gray-700 mb-6"
                >
                  Per completare il tuo pagamento, gentilmente eseguire un bonifico bancario utilizzando i dati seguenti. Verifica che il bonifico venga effettuato da un conto con lo stesso nome utilizzato durante la registrazione dell'account per evitare rallentamenti.
                </motion.p>
                
                <motion.div 
                  variants={fadeIn}
                  custom={4}
                  className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 mb-6"
                >
                  <h3 className="font-semibold text-gray-900 mb-4">Dati Bancari:</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Nome Banca</p>
                      <p className="font-medium">MockBank Inc.</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Destinatario</p>
                      <p className="font-medium">Marrocco ART</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Nr. Account</p>
                      <p className="font-medium">1234567890</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">IBAN</p>
                      <p className="font-medium">IT60X0542811101000000123456</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">SWIFT Code</p>
                      <p className="font-medium">MOCKBICXXX</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border border-gray-100">
                      <p className="text-sm text-gray-500 mb-1">Referenza (Memo)</p>
                      <p className="font-medium">{orderData.stripePaymentId}</p>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  variants={fadeIn}
                  custom={5}
                  className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400 text-yellow-800"
                >
                  <p className="flex items-center font-semibold mb-2">
                    <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Importante
                  </p>
                  <p className="text-sm">
                    Una volta completato il bonifico potete chiudere questa pagina. Riceverete un'email di conferma non appena il pagamento verrà convalidato. Per qualsiasi domanda o dubbio non esitate a <a href="/contatti" className="underline font-semibold hover:text-yellow-900">contattarci</a>.
                  </p>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div 
                variants={fadeIn}
                custom={2}
                className="text-center p-6"
              >
                <p className="text-gray-600">Seleziona un metodo di pagamento per continuare</p>
              </motion.div>
            )
          )}
        </motion.div>
      </motion.div>
    );
  };

  const components = [
    <PersonalData key="personal" />, 
    <PaymentMethod key="payment" />, 
    <CompletePayment key="complete" />
  ];

  async function fetchPaymentData() {
    try {
      const accessToken = Cookies.get('accessToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}user/orderPaymentData?stripePaymentId=${paymentId}&accessToken=${accessToken}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setPaymentData(data);
      setLoading(false); // Stop the loading spinner once data is fetched
    } catch (error) {
      setErrorMessage("Failed to fetch payment data. Please try again later.");
      setLoading(false);
    }
  }

  async function fetchOrderData() {
    try {
      const accessToken = Cookies.get('accessToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}user/order?stripePaymentId=${paymentId}&accessToken=${accessToken}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setOrderDAta(data);
      setLoading(false); // Stop the loading spinner once data is fetched
    } catch (error) {
      setErrorMessage("Failed to fetch order data. Please try again later.");
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPaymentData();
    fetchOrderData();
  }, [paymentId]);

  const stripePk = process.env.REACT_APP_STRIPE_PK;
  const stripePromise = loadStripe(stripePk);

  const options = paymentData ? { clientSecret: paymentData.stripePaymentSecret } : null;

  if(errorMessage) {
    return <NotFound />
  }

  if(orderData?.status === "paid") {
    navigate(`/order/${paymentId}`)
  }

  return (
    <section className="relative min-h-screen bg-gray-50 w-full">
      <Layout>
        <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Steps Progress Bar */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex justify-center mb-6">
              <nav className="flex items-center">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex flex-col items-center justify-center ${
                        index <= showComponent 
                          ? 'cursor-pointer' 
                          : 'cursor-not-allowed opacity-60'
                      }`}
                      onClick={() => handleStepClick(index)}
                      disabled={!completedSteps.has(index - 1) && index > showComponent}
                    >
                      <div className={`
                        flex items-center justify-center w-12 h-12 rounded-full border-2 
                        ${index === showComponent 
                          ? 'bg-white border-[#406580] text-[#406580]' 
                          : index < showComponent 
                            ? 'bg-[#406580] border-[#406580] text-white' 
                            : 'bg-white border-gray-300 text-gray-400'
                        }
                      `}>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ 
                            type: "spring",
                            stiffness: 260,
                            damping: 20
                          }}
                        >
                          {step.icon}
                        </motion.div>
                      </div>
                      <span className={`
                        mt-2 text-sm font-medium
                        ${index === showComponent 
                          ? 'text-[#406580]' 
                          : index < showComponent 
                            ? 'text-gray-700' 
                            : 'text-gray-400'
                        }
                      `}>
                        {step.title}
                      </span>
                    </motion.button>
  
                    {/* Connector line between steps */}
                    {index < steps.length - 1 && (
                      <div className="w-20 mx-1">
                        <div className="h-1 relative bg-gray-200">
                          <motion.div 
                            className="absolute inset-0 bg-[#406580]"
                            initial={{ width: "0%" }}
                            animate={{ 
                              width: index < showComponent ? "100%" : "0%" 
                            }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </motion.div>
  
          {/* Content Area with AnimatePresence for smooth transitions */}
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center min-h-[400px]"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 border-4 border-gray-100 border-t-[#406580] rounded-full mb-4"
                />
                <p className="text-gray-500 font-medium">Caricamento informazioni...</p>
              </motion.div>
            ) : errorMessage ? (
              <motion.div 
                key="error"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -20 }}
                className="bg-white p-8 rounded-lg shadow-sm border border-red-100 max-w-lg mx-auto text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-2 text-gray-900">Si è verificato un errore</h2>
                <p className="text-gray-600 mb-6">{errorMessage}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570] transition-colors"
                >
                  Riprova
                </button>
              </motion.div>
            ) : orderData && paymentData ? ( 
              <motion.div 
                key={`content-${showComponent}`}
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {components[showComponent]}
              </motion.div>
            ) : null}
          </AnimatePresence>
  
          {/* Navigation Buttons */}
          {!loading && !errorMessage && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="flex justify-center mt-10 space-x-4"
            >
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                disabled={showComponent === 0}
                onClick={() => setShowComponent(showComponent - 1)}
                className={`
                  flex items-center px-5 py-2.5 rounded-lg transition-all focus:outline-none
                  ${showComponent === 0 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }
                `}
              >
                <FiArrowLeft className="mr-2" /> Indietro
              </motion.button>
              
              {showComponent < steps.length - 1 ? (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleNextStep}
                  className="flex items-center px-5 py-2.5 bg-[#406580] text-white rounded-lg hover:bg-[#305570] transition-all shadow-sm"
                >
                  Continua <FiArrowRight className="ml-2" />
                </motion.button>
              ) : selectedPaymentMethod === "creditCard" ? (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm"
                  onClick={() => document.querySelector('form')?.requestSubmit()}
                >
                  Paga Ora <FiCheckCircle className="ml-2" />
                </motion.button>
              ) : (
                <Link to={`/order/${paymentId}`}>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm"
                  >
                    Visualizza Ordine <FiArrowRight className="ml-2" />
                  </motion.button>
                </Link>
              )}
            </motion.div>
          )}
  
          {/* Order Security Badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex justify-center mt-8"
          >
            <div className="flex items-center text-gray-500 text-xs">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Pagamento sicuro e protetto
            </div>
          </motion.div>
        </div>
      </Layout>
    </section>
  );
}

export default Payment;